import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Select, Spin } from 'antd'
import { useDebouncedCallback } from 'use-debounce'
import { SEARCH_DEBOUNCE_MS, SEARCH_MIN_CHARS } from '../constants'
import { getApiErrorMessage } from '../utils'


const AsyncSelect = ({
	value,
	onChange,
	fetcher,
	mapOption,
	placeholder,
	mode,
	disabled,
	allowClear = true,
	status,
	minChars = SEARCH_MIN_CHARS,
	extraOptions = [],
	className = 'w-full',
	onError,
}) => {
	const [options, setOptions] = useState([])
	const [loading, setLoading] = useState(false)
	const [loaded, setLoaded] = useState(false)
	const requestId = useRef(0)
	const mountedRef = useRef(true)

	useEffect(() => () => (mountedRef.current = false), [])

	const load = useCallback(
		(search) => {
			const id = ++requestId.current
			setLoading(true)
			fetcher(search)
				.then((list) => {
					if (!mountedRef.current || id !== requestId.current) return
					setOptions((list || []).map(mapOption))
					setLoaded(true)
				})
				.catch((err) => {
					if (!mountedRef.current || id !== requestId.current) return
					setOptions([])
					onError?.(getApiErrorMessage(err))
				})
				.finally(() => {
					if (mountedRef.current && id === requestId.current) setLoading(false)
				})
		},
		[fetcher, mapOption, onError],
	)

	const debouncedLoad = useDebouncedCallback(load, SEARCH_DEBOUNCE_MS)

	const handleSearch = (text) => {
		const search = text.trim()
		if (search && search.length < minChars) return
		debouncedLoad(search || undefined)
	}

	const handleOpen = (open) => {
		if (open && !loaded && !loading) load(undefined)
	}

	const keys = new Set(options.map((o) => o.value))
	const merged = [
		...extraOptions.filter((o) => o && !keys.has(o.value)),
		...options,
	]

	return (
		<Select
			className={className}
			value={value ?? undefined}
			mode={mode}
			disabled={disabled}
			status={status}
			allowClear={allowClear}
			showSearch
			filterOption={false}
			optionLabelProp="title"
			options={merged}
			onSearch={handleSearch}
			onOpenChange={handleOpen}
			onChange={(next, option) => onChange?.(next, option)}
			placeholder={placeholder}
			notFoundContent={
				loading ? (
					<div className="flex justify-center py-3">
						<Spin size="small" />
					</div>
				) : (
					<div className="py-3 text-center text-xs text-slate-400">
						{`Topilmadi · kamida ${minChars} belgi kiriting`}
					</div>
				)
			}
			loading={loading}
		/>
	)
}

export const OptionRow = ({ title, subtitle, right }) => (
	<div className="flex items-center justify-between gap-3">
		<div className="min-w-0">
			<div className="truncate text-[13px] text-slate-800">{title}</div>
			{subtitle && (
				<div className="truncate text-[11px] text-slate-400">{subtitle}</div>
			)}
		</div>
		{right && <div className="shrink-0 text-[11px] text-slate-400">{right}</div>}
	</div>
)

export default AsyncSelect
